import React from 'react'
import { useTranslation } from 'react-i18next'
import { AnalysisSectionHeader } from '../../../../../AnalysisSectionHeader'
import {
  AnalysisMetricsGrid,
  AnalysisMetricCard,
  MetricHeader,
  MetricIcon,
  MetricTitle,
  MetricValue,
  MetricSubtext,
} from '../../../../../atoms/AnalysisMetricCard'
import { InfoTooltip } from '../../../../../InfoTooltip'
import { TooltipMetricExplanation } from '../../../../../TooltipMetricExplanation'
import { SectionTitle } from '../../../../../atoms/SectionTitle'
import { AnalysisTabContentContainer } from '../../../../../molecules/SectionAnalysisStyles'
import { Icon } from '../../../../../atoms/Icon'
import { Box } from '../../../../../atoms/Box'
import { useAnalysisMode } from '../../../contexts/AnalysisModeContext'
import {
  BuildingInformation,
  AdministrativeDetailsSection,
  DwellingsSection,
  LandsSection,
  EnergySection,
  ViewSection,
  TransportSection,
} from '../../../../BuildingPropertyPanel'
import {
  Container,
  ActivitySection,
  ActivityItem,
  ActivityIcon,
  ActivityContent,
  ActivityTitle,
  ActivityDetail,
} from './BuildingSummaryTab.styles'

/**
 * Shared BuildingSummaryTab for analysis views.
 * Displays key building metrics, 7 rich information sections, and recent activity.
 * Matches the main app's SummaryTab design with BuildingPropertyPanel sections.
 * Reads entity data from AnalysisModeContext.
 */
export const BuildingSummaryTab: React.FC = () => {
  const { t } = useTranslation()
  const { entity } = useAnalysisMode()
  const building = entity

  if (!building) {
    return <div>{t('buildingAnalysis.summaryTab.noData')}</div>
  }

  const formatArea = (area?: number) =>
    area ? `${area.toLocaleString()} m²` : 'N/A'
  const formatNumber = (num?: number) => (num ? num.toLocaleString() : 'N/A')

  const groundArea = building.area || 0
  const floorCount = building.floor_nb || 0
  const buildingYear = building.building_year || 0
  const buildingPeriod = building.building_period || ''
  const renovationYear = building.renovation_year || 0
  const renovationPeriod = building.renovation_period || ''
  const buildingVolume =
    building.building_volume || building.volume_estimate || 0
  const height = building.height || building.height_estimate || 0

  // Calculate residential area from buildings_administrative dwellings
  const residentialArea =
    building.buildings_administrative?.reduce((total: number, admin: any) => {
      const adminArea =
        admin.addresses?.reduce((addrTotal: number, addr: any) => {
          const dwellingArea =
            addr.dwellings?.reduce(
              (dwTotal: number, dw: any) => dwTotal + (dw.area || 0),
              0
            ) || 0
          return addrTotal + dwellingArea
        }, 0) || 0
      return total + adminArea
    }, 0) || 0

  const hasRecentTransactions = building.transaction_last_year
    ? new Date().getFullYear() - building.transaction_last_year <= 2
    : false
  const hasRecentPermits = building.construction_permit_last_year
    ? new Date().getFullYear() - building.construction_permit_last_year <= 2
    : false
  const hasRecentListings =
    building.listing_sale_last_year || building.listing_rent_last_year

  return (
    <Container>
      <AnalysisSectionHeader
        title={t('buildingAnalysis.summaryTab.title')}
        subtitle={t('buildingAnalysis.summaryTab.subtitle', {
          address: building.address_full || 'this building',
          municipality: building.municipality_name || '',
        })}
      />

      <AnalysisTabContentContainer>
        {/* Key Metrics */}
        <AnalysisMetricsGrid>
          <AnalysisMetricCard>
            <MetricHeader>
              <MetricIcon color="#3b82f6">
                <Icon name="square" size="small" />
              </MetricIcon>
              <MetricTitle>
                {t('buildingAnalysis.summaryTab.metrics.groundArea.title')}
                <InfoTooltip>
                  <TooltipMetricExplanation
                    title={t(
                      'buildingAnalysis.summaryTab.metrics.groundArea.tooltip.title'
                    )}
                    shortDescription={t(
                      'buildingAnalysis.summaryTab.metrics.groundArea.tooltip.shortDescription'
                    )}
                    calculation={t(
                      'buildingAnalysis.summaryTab.metrics.groundArea.tooltip.calculation'
                    )}
                    importance={t(
                      'buildingAnalysis.summaryTab.metrics.groundArea.tooltip.importance'
                    )}
                    limitations={t(
                      'buildingAnalysis.summaryTab.metrics.groundArea.tooltip.limitations'
                    )}
                    source={t(
                      'buildingAnalysis.summaryTab.metrics.groundArea.tooltip.source'
                    )}
                  />
                </InfoTooltip>
              </MetricTitle>
            </MetricHeader>
            <Box mt="8px">
              <MetricValue>{formatArea(groundArea)}</MetricValue>
              {residentialArea > 0 && (
                <MetricSubtext>
                  {t('buildingAnalysis.summaryTab.metrics.groundArea.subtext', {
                    value: residentialArea.toLocaleString(),
                  })}
                </MetricSubtext>
              )}
            </Box>
          </AnalysisMetricCard>

          <AnalysisMetricCard>
            <MetricHeader>
              <MetricIcon color="#8b5cf6">
                <Icon name="layers" size="small" />
              </MetricIcon>
              <MetricTitle>
                {t('buildingAnalysis.summaryTab.metrics.floors.title')}
                <InfoTooltip>
                  <TooltipMetricExplanation
                    title={t(
                      'buildingAnalysis.summaryTab.metrics.floors.tooltip.title'
                    )}
                    shortDescription={t(
                      'buildingAnalysis.summaryTab.metrics.floors.tooltip.shortDescription'
                    )}
                    calculation={t(
                      'buildingAnalysis.summaryTab.metrics.floors.tooltip.calculation'
                    )}
                    importance={t(
                      'buildingAnalysis.summaryTab.metrics.floors.tooltip.importance'
                    )}
                    source={t(
                      'buildingAnalysis.summaryTab.metrics.floors.tooltip.source'
                    )}
                  />
                </InfoTooltip>
              </MetricTitle>
            </MetricHeader>
            <Box mt="8px">
              <MetricValue>{formatNumber(floorCount)}</MetricValue>
              {height > 0 && (
                <MetricSubtext>
                  {t('buildingAnalysis.summaryTab.metrics.floors.subtext', {
                    value: height,
                  })}
                </MetricSubtext>
              )}
            </Box>
          </AnalysisMetricCard>

          <AnalysisMetricCard>
            <MetricHeader>
              <MetricIcon color="#f59e0b">
                <Icon name="calendar" size="small" />
              </MetricIcon>
              <MetricTitle>
                {t('buildingAnalysis.summaryTab.metrics.yearBuilt.title')}
                <InfoTooltip>
                  <TooltipMetricExplanation
                    title={t(
                      'buildingAnalysis.summaryTab.metrics.yearBuilt.tooltip.title'
                    )}
                    shortDescription={t(
                      'buildingAnalysis.summaryTab.metrics.yearBuilt.tooltip.shortDescription'
                    )}
                    importance={t(
                      'buildingAnalysis.summaryTab.metrics.yearBuilt.tooltip.importance'
                    )}
                    source={t(
                      'buildingAnalysis.summaryTab.metrics.yearBuilt.tooltip.source'
                    )}
                  />
                </InfoTooltip>
              </MetricTitle>
            </MetricHeader>
            <Box mt="8px">
              <MetricValue>
                {buildingYear || buildingPeriod || 'N/A'}
              </MetricValue>
              {(renovationYear || renovationPeriod) && (
                <MetricSubtext>
                  {t('buildingAnalysis.summaryTab.metrics.yearBuilt.subtext', {
                    value: renovationYear || renovationPeriod,
                  })}
                </MetricSubtext>
              )}
            </Box>
          </AnalysisMetricCard>

          {buildingVolume > 0 && (
            <AnalysisMetricCard>
              <MetricHeader>
                <MetricIcon color="#06b6d4">
                  <Icon name="box" size="small" />
                </MetricIcon>
                <MetricTitle>
                  {t(
                    'buildingAnalysis.summaryTab.metrics.buildingVolume.title'
                  )}
                  <InfoTooltip>
                    <TooltipMetricExplanation
                      title={t(
                        'buildingAnalysis.summaryTab.metrics.buildingVolume.tooltip.title'
                      )}
                      shortDescription={t(
                        'buildingAnalysis.summaryTab.metrics.buildingVolume.tooltip.shortDescription'
                      )}
                      calculation={t(
                        'buildingAnalysis.summaryTab.metrics.buildingVolume.tooltip.calculation'
                      )}
                      importance={t(
                        'buildingAnalysis.summaryTab.metrics.buildingVolume.tooltip.importance'
                      )}
                      source={t(
                        'buildingAnalysis.summaryTab.metrics.buildingVolume.tooltip.source'
                      )}
                    />
                  </InfoTooltip>
                </MetricTitle>
              </MetricHeader>
              <Box mt="8px">
                <MetricValue>
                  {formatNumber(buildingVolume)}{' '}
                  {t(
                    'buildingAnalysis.summaryTab.metrics.buildingVolume.subtext'
                  )}
                </MetricValue>
              </Box>
            </AnalysisMetricCard>
          )}
        </AnalysisMetricsGrid>

        {/* BuildingPropertyPanel Information Sections */}
        <Box style={{ display: 'flex', flexDirection: 'column' }}>
          <BuildingInformation building={building} />
          <AdministrativeDetailsSection building={building} />
          <DwellingsSection building={building} />
          <LandsSection lands={building.lands || []} />
          <EnergySection building={building} />
          <ViewSection building={building} />
          <TransportSection building={building} />
        </Box>

        {/* Recent Activity */}
        {(hasRecentTransactions || hasRecentPermits || hasRecentListings) && (
          <Box mt="32px">
            <SectionTitle>
              {t('buildingAnalysis.summaryTab.sections.recentActivity')}
            </SectionTitle>
            <ActivitySection>
              {hasRecentTransactions && (
                <ActivityItem>
                  <ActivityIcon color="#3b82f6">
                    <Icon name="receipt" size="small" />
                  </ActivityIcon>
                  <ActivityContent>
                    <ActivityTitle>
                      {t(
                        'buildingAnalysis.summaryTab.activity.recentTransactions'
                      )}
                    </ActivityTitle>
                    <ActivityDetail>
                      {t(
                        'buildingAnalysis.summaryTab.activity.transactionDetail',
                        {
                          count: building.lands_transaction_number,
                          year: building.transaction_last_year,
                        }
                      )}
                    </ActivityDetail>
                  </ActivityContent>
                </ActivityItem>
              )}

              {hasRecentPermits && (
                <ActivityItem>
                  <ActivityIcon color="#10b981">
                    <Icon name="file-text" size="small" />
                  </ActivityIcon>
                  <ActivityContent>
                    <ActivityTitle>
                      {t(
                        'buildingAnalysis.summaryTab.activity.buildingPermits'
                      )}
                    </ActivityTitle>
                    <ActivityDetail>
                      {t('buildingAnalysis.summaryTab.activity.permitDetail', {
                        count: building.lands_building_permit_number,
                        year: building.construction_permit_last_year,
                      })}
                    </ActivityDetail>
                  </ActivityContent>
                </ActivityItem>
              )}

              {building.listing_sale_number &&
                building.listing_sale_number > 0 && (
                  <ActivityItem>
                    <ActivityIcon color="#f59e0b">
                      <Icon name="tag" size="small" />
                    </ActivityIcon>
                    <ActivityContent>
                      <ActivityTitle>
                        {t('buildingAnalysis.summaryTab.activity.saleListings')}
                      </ActivityTitle>
                      <ActivityDetail>
                        {t(
                          'buildingAnalysis.summaryTab.activity.listingDetail',
                          {
                            count: building.listing_sale_number,
                            year: building.listing_sale_last_year || '',
                          }
                        )}
                      </ActivityDetail>
                    </ActivityContent>
                  </ActivityItem>
                )}

              {building.listing_rent_number &&
                building.listing_rent_number > 0 && (
                  <ActivityItem>
                    <ActivityIcon color="#8b5cf6">
                      <Icon name="key" size="small" />
                    </ActivityIcon>
                    <ActivityContent>
                      <ActivityTitle>
                        {t(
                          'buildingAnalysis.summaryTab.activity.rentalListings'
                        )}
                      </ActivityTitle>
                      <ActivityDetail>
                        {t(
                          'buildingAnalysis.summaryTab.activity.listingDetail',
                          {
                            count: building.listing_rent_number,
                            year: building.listing_rent_last_year || '',
                          }
                        )}
                      </ActivityDetail>
                    </ActivityContent>
                  </ActivityItem>
                )}
            </ActivitySection>
          </Box>
        )}
      </AnalysisTabContentContainer>
    </Container>
  )
}
